/**
 * Diversifies the slide order within each category-feature-showcase
 * carousel so consecutive slides rarely belong to the same product series.
 * Liquid renders the slides in plain, unmodified collection order and only
 * stamps each card with its series (`data-series`, set in
 * sections/category-feature-showcase.liquid from the product's tag ending
 * in "-serisi"). All the actual reordering happens here, client-side, with
 * native Array methods - this replaced an earlier Liquid-side attempt that
 * computed reordered array indices with `array[i]`, which proved unreliable
 * in this Liquid environment and occasionally handed quick-add a blank
 * product.
 *
 * Runs once per page load. Cards without a recognized series share a single
 * "no series" bucket rather than being scattered into their own group each.
 */

/**
 * Interleaves cards round-robin by series so no two cards from the same
 * series end up adjacent (beyond what the number of distinct series
 * allows).
 *
 * @param {HTMLElement[]} cards
 * @returns {HTMLElement[]}
 */
function diversifyBySeries(cards) {
  /** @type {Map<string, HTMLElement[]>} */
  const buckets = new Map();
  /** @type {string[]} */
  const bucketOrder = [];

  for (const card of cards) {
    const key = card.dataset.series || '__no_series__';

    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      bucketOrder.push(key);
    }
    bucket.push(card);
  }

  // Only one series present (or none tagged) - nothing to interleave.
  if (bucketOrder.length <= 1) return cards;

  /** @type {HTMLElement[]} */
  const result = [];
  let addedThisPass = true;

  while (addedThisPass) {
    addedThisPass = false;

    for (const key of bucketOrder) {
      const bucket = buckets.get(key);
      const next = bucket?.shift();

      if (next) {
        result.push(next);
        addedThisPass = true;
      }
    }
  }

  return result;
}

/**
 * @param {HTMLElement} wrapper
 */
function diversifyCarousel(wrapper) {
  const scroller = wrapper.querySelector('slideshow-slides');
  if (!scroller) return;

  const cards = Array.from(scroller.children).filter(
    (el) => el instanceof HTMLElement && el.classList.contains('category-feature-showcase__card')
  );

  if (cards.length < 2) return;

  const reordered = diversifyBySeries(cards);

  // Moves the existing nodes into their new order (append() relocates
  // nodes that are already in the document rather than cloning them), so
  // this only ever reorders - nothing is recreated or re-rendered.
  scroller.append(...reordered);

  // If slideshow-component had already finished its own setup by the time
  // this runs, its current-slide index and scroll position would now point
  // at the wrong slide after the reorder above - resync them. If setup
  // hasn't run yet, select() safely no-ops (it bails out while its
  // internal scroller isn't ready yet), and the slideshow's own upcoming
  // setup just reads the already-reordered slides from the start.
  const slideshow = wrapper.querySelector('slideshow-component');
  if (slideshow && 'select' in slideshow && typeof slideshow.select === 'function') {
    slideshow.select(0, undefined, { animate: false });
  }
}

document.querySelectorAll('.category-feature-showcase__carousel-wrapper').forEach((wrapper) => {
  if (wrapper instanceof HTMLElement) diversifyCarousel(wrapper);
});
